import React from 'react';
import { Box, Stack, Button } from '@mui/material';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';

const font = '"Poppins", sans-serif';

const CustomPagination = ({ currentPage = 1, totalPages = 10, onPageChange, sx = {} }) => {
  // Generate pagination range with ellipses
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      if (!pages.includes(totalPages)) {
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const pages = getPageNumbers();

  const handlePageClick = (page) => {
    if (page !== '...' && onPageChange) {
      onPageChange(page);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        mt: 4,
        flexWrap: 'wrap',
        gap: 2,
        ...sx
      }}
    >
      {/* Previous Page Button */}
      <Button
        onClick={() => currentPage > 1 && handlePageClick(currentPage - 1)}
        disabled={currentPage === 1}
        startIcon={<KeyboardArrowLeftRoundedIcon sx={{ fontSize: '18px' }} />}
        sx={{
          color: '#475569',
          textTransform: 'none',
          fontFamily: font,
          fontWeight: 600,
          fontSize: '13px',
          '&.Mui-disabled': { color: '#cbd5e1' },
          '&:hover': { bgcolor: 'transparent', color: '#1e293b' }
        }}
      >
        Prev page
      </Button>

      {/* Number circles */}
      <Stack direction="row" spacing={1} alignItems="center">
        {pages.map((page, index) => {
          const isActive = page === currentPage;
          const isEllipsis = page === '...';
          
          return (
            <Box
              key={index}
              onClick={() => handlePageClick(page)}
              sx={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                fontFamily: font,
                fontSize: '13.5px',
                fontWeight: isActive ? 700 : 550,
                cursor: isEllipsis ? 'default' : 'pointer',
                color: isActive ? '#FF502D' : isEllipsis ? '#94a3b8' : '#64748b',
                bgcolor: isActive ? '#FFEBEA' : 'transparent',
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: isEllipsis ? 'transparent' : isActive ? '#FFEBEA' : '#f1f5f9'
                }
              }}
            >
              {page}
            </Box>
          );
        })}
      </Stack>

      {/* Next Page Button */}
      <Button
        onClick={() => currentPage < totalPages && handlePageClick(currentPage + 1)}
        disabled={currentPage === totalPages}
        endIcon={<KeyboardArrowRightRoundedIcon sx={{ fontSize: '18px' }} />}
        sx={{
          color: '#475569',
          textTransform: 'none',
          fontFamily: font,
          fontWeight: 600,
          fontSize: '13px',
          '&.Mui-disabled': { color: '#cbd5e1' },
          '&:hover': { bgcolor: 'transparent', color: '#1e293b' }
        }}
      >
        Next page
      </Button>
    </Box>
  );
};

export default CustomPagination;
